import { useEffect, useCallback } from 'react';

/**
 * Keyboard Shortcuts Hook
 *
 * Manages global keyboard shortcuts for the application.
 * Currently supports:
 * - Cmd+K / Ctrl+K: Focus search
 * - Escape: Close modals/search
 *
 * Usage:
 * useKeyboardShortcuts({
 *   onSearchFocus: () => searchInputRef.current?.focus(),
 *   onEscape: () => setIsSearchOpen(false),
 * });
 */
export const useKeyboardShortcuts = ({
  onSearchFocus,
  onEscape,
  enabled = true,
}) => {
  const handleKeyDown = useCallback(
    (event) => {
      if (!enabled) return;

      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modifierKey = isMac ? event.metaKey : event.ctrlKey;

      // Cmd+K or Ctrl+K - Focus search
      if (modifierKey && event.key === 'k') {
        event.preventDefault();
        if (onSearchFocus) {
          onSearchFocus();
        }
      }

      // Escape - Close search/modals
      if (event.key === 'Escape') {
        if (onEscape) {
          onEscape();
        }
      }
    },
    [enabled, onSearchFocus, onEscape]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);
};

/**
 * Get keyboard shortcut display string based on platform
 */
export const getShortcutDisplay = (shortcut) => {
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;

  const shortcuts = {
    search: isMac ? '⌘K' : 'Ctrl+K',
    escape: 'Esc',
  };

  return shortcuts[shortcut] || shortcut;
};

export default useKeyboardShortcuts;
